import {
  useEffect, useState, memo, useMemo
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
  Grid,
  Divider,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router';
import ROUTE from 'src/utils/routeConstants';
import { useDispatch, useSelector } from 'react-redux';
import { TRANSACTION_TYPES, USER_ROLES } from 'src/utils/constants';
import {
  getTransactionList,
} from 'src/redux/transactionSlice';
import Loader from 'src/common/Loader';
import EnhancedTableHead, { getComparator } from 'src/common/EnhancedTableHead';
import { getAlignByColumnId, transactionTableHeaders } from 'src/utils/tableHeaders';
import {
  getFormattedDate,
  getFuelQuantity,
  getTransactionQueryParams,
  hasAdminLikeAccess,
  removeLeadingZeros,
} from 'src/utils/getInitials';
import useAuth from 'src/context/AuthContext';
import CustomPagination from 'src/common/CustomPagination';
import {
  getTableActionIconButtonSx,
  TABLE_ACTION_ICON_SX
} from 'src/common/tableActionStyles';

// Mobile Card View Component for Purchase Transactions
const PurchaseMobileCard = memo(({
  transaction, onViewClick, theme, disabled
}) => (
  <Card
    sx={{
      mb: 2,
      p: { xs: 2, sm: 2.5 },
      borderRadius: 3,
      background: 'linear-gradient(180deg, #fff, #f9f9f9)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      border: `1px solid ${theme.palette.divider}`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      },
    }}
  >
    {/* Header Section */}
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 2,
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            fontSize: { xs: '1rem', sm: '1.125rem' },
            mb: 0.5,
          }}
        >
          ID:
          {' '}
          {transaction.id}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: { xs: '0.813rem', sm: '0.875rem' },
          }}
        >
          {transaction.site_name || 'N/A'}
        </Typography>
      </Box>
    </Box>

    <Divider sx={{ mb: 2 }} />

    {/* Dates Grid */}
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={6}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Order Date
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {getFormattedDate(transaction.order_date) || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={6}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Delivery Date
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {getFormattedDate(transaction.delivery_date) || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Invoice Number
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.invoice_number || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Supplier
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.supplier || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Received Tank
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.received_tank_name || 'N/A'}
          </Typography>
        </Box>
      </Grid>
    </Grid>

    {/* Quantity & Price Stats */}
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={6}>
        <Box
          sx={{
            textAlign: 'center',
            p: 1.5,
            borderRadius: 2,
            bgcolor: theme.palette.primary.lighter || 'rgba(25, 118, 210, 0.08)',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Quantity
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: theme.palette.primary.main,
              fontSize: { xs: '1.125rem', sm: '1.25rem' },
            }}
          >
            {getFuelQuantity(transaction.actual_filled || transaction.quantity) || '0 L'}
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={6}>
        <Box
          sx={{
            textAlign: 'center',
            p: 1.5,
            borderRadius: 2,
            bgcolor: theme.palette.success.lighter || 'rgba(46, 125, 50, 0.08)',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Price/Litre
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: theme.palette.success.main,
              fontSize: { xs: '1.125rem', sm: '1.25rem' },
            }}
          >
            ₹
            {transaction.price_per_litre || '0'}
          </Typography>
        </Box>
      </Grid>
    </Grid>

    {/* View Button */}
    <Button
      variant="contained"
      fullWidth
      disabled={disabled}
      sx={{
        borderRadius: 2,
        textTransform: 'none',
        py: { xs: 1, sm: 1.25 },
        fontSize: { xs: '0.875rem', sm: '0.938rem' },
        fontWeight: 600,
        background: 'linear-gradient(90deg, #1976d2, #115293)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        '&:hover': {
          background: 'linear-gradient(90deg, #115293, #1976d2)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        },
      }}
      onClick={() => onViewClick(transaction.id)}
    >
      View Details
    </Button>
  </Card>
));

PurchaseMobileCard.displayName = 'PurchaseMobileCard';

PurchaseMobileCard.propTypes = {
  transaction: PropTypes.object.isRequired,
  onViewClick: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

// Mobile Card View Component for Sales Transactions
const SalesMobileCard = memo(({
  transaction, onViewClick, theme, disabled
}) => (
  <Card
    sx={{
      mb: 2,
      p: { xs: 2, sm: 2.5 },
      borderRadius: 3,
      background: 'linear-gradient(180deg, #fff, #f9f9f9)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      border: `1px solid ${theme.palette.divider}`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      },
    }}
  >
    {/* Header Section */}
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 2,
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            fontSize: { xs: '1rem', sm: '1.125rem' },
            mb: 0.5,
          }}
        >
          Order:
          {' '}
          {transaction.order_id || 'N/A'}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: { xs: '0.813rem', sm: '0.875rem' },
          }}
        >
          {getFormattedDate(transaction.start_time) || 'N/A'}
        </Typography>
      </Box>
    </Box>

    <Divider sx={{ mb: 2 }} />

    {/* Transaction Details */}
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Site & Location
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500, mb: 0.5 }}
          >
            {transaction.site_name || 'N/A'}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary }}
          >
            {transaction.address || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={6} sm={4}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Nozzle
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.nozzle_num || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={6} sm={4}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Card 1
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {removeLeadingZeros(transaction.card_num_1) || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={6} sm={4}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Card 2
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {removeLeadingZeros(transaction.card_num_2) || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Vehicle Number
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.vehicle_number || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Odometer Reading
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.odometer_entry ?? 'N/A'}
          </Typography>
        </Box>
      </Grid>
    </Grid>

    {/* Quantity Badge */}
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: theme.palette.primary.lighter || 'rgba(25, 118, 210, 0.08)',
        textAlign: 'center',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.text.secondary,
          fontWeight: 600,
          display: 'block',
          mb: 0.5,
        }}
      >
        Quantity Dispensed
      </Typography>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: theme.palette.primary.main,
          fontSize: { xs: '1.125rem', sm: '1.25rem' },
        }}
      >
        {getFuelQuantity(transaction.quantity) || '0 L'}
      </Typography>
    </Box>

    {/* View Button */}
    <Button
      variant="contained"
      fullWidth
      disabled={disabled}
      sx={{
        borderRadius: 2,
        textTransform: 'none',
        py: { xs: 1, sm: 1.25 },
        fontSize: { xs: '0.875rem', sm: '0.938rem' },
        fontWeight: 600,
        background: 'linear-gradient(90deg, #1976d2, #115293)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        '&:hover': {
          background: 'linear-gradient(90deg, #115293, #1976d2)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        },
      }}
      onClick={() => onViewClick(transaction.id)}
    >
      View Details
    </Button>
  </Card>
));

SalesMobileCard.displayName = 'SalesMobileCard';

SalesMobileCard.propTypes = {
  transaction: PropTypes.object.isRequired,
  onViewClick: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

const TransactionListTable = ({
  isSales,
  refreshData,
  searchText,
  dateFilter,
  siteFilter,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();
  const { getUser } = useAuth();
  const user = getUser();
  const hasAdminAccess = hasAdminLikeAccess(getUser);
  const isApprover = user?.role === USER_ROLES.siteManager;
  const {
    transactionList,
    transactionType,
    totalCount,
    loading,
  } = useSelector((state) => state.transactions);

  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 900px

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('order_id');
  const activeTransactionHeaders = useMemo(
    () => transactionTableHeaders[transactionType] || [],
    [transactionType]
  );
  const transactionAlignById = useMemo(
    () => getAlignByColumnId(activeTransactionHeaders),
    [activeTransactionHeaders]
  );

  useEffect(() => {
    const orderById = transactionType === TRANSACTION_TYPES[0].id ? 'order_id' : 'id';
    setOrderBy(orderById);
  }, [transactionType]);

  useEffect(() => {
    setPage(0);
    dispatch(
      getTransactionList({
        transactionType,
        limit,
        pageNo: 1,
        params: getTransactionQueryParams(
          isSales,
          searchText,
          dateFilter,
          siteFilter
        ),
      })
    );
  }, [
    dispatch,
    transactionType,
    limit,
    refreshData,
    isSales,
    searchText,
    dateFilter,
    siteFilter
  ]);

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPage(0);
    setLimit(newLimit);
  };

  const handlePageChange = (e, newPage) => {
    setPage(newPage);
    dispatch(
      getTransactionList({
        transactionType,
        limit,
        pageNo: newPage + 1,
        params: getTransactionQueryParams(
          isSales,
          searchText,
          dateFilter,
          siteFilter
        ),
      })
    );
  };

  const viewTransactionInfo = (transactionId) => {
    history.push(`${ROUTE.transactions}/${transactionId}`);
  };

  const handleRequestSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const isDetailsDisabled = () => {
    let disableDetails = !hasAdminAccess;
    if (transactionType === TRANSACTION_TYPES[1].id && isApprover) {
      disableDetails = false;
    }
    return disableDetails;
  };

  const renderTableData = (transaction) => {
    if (transactionType === TRANSACTION_TYPES[1].id) {
      return (
        <>
          <TableCell align={transactionAlignById.id} sx={{ fontSize: { sm: '0.813rem', md: '0.875rem' } }}>
            {transaction.id}
          </TableCell>
          <TableCell align={transactionAlignById.order_date} sx={{ fontSize: { sm: '0.813rem', md: '0.875rem' } }}>
            {getFormattedDate(transaction.order_date)}
          </TableCell>
          <TableCell align={transactionAlignById.delivery_date} sx={{ fontSize: { sm: '0.813rem', md: '0.875rem' } }}>
            {getFormattedDate(transaction.delivery_date)}
          </TableCell>
          <TableCell align={transactionAlignById.site_name} sx={{ fontSize: { sm: '0.813rem', md: '0.875rem' } }}>
            {transaction.site_name}
          </TableCell>
          <TableCell align={transactionAlignById.invoice_number} sx={{ fontSize: { sm: '0.813rem', md: '0.875rem' } }}>
            {transaction.invoice_number}
          </TableCell>
          <TableCell align={transactionAlignById.quantity}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontSize: { sm: '0.875rem', md: '0.938rem' },
                }}
              >
                {getFuelQuantity(
                  transaction.actual_filled || transaction.quantity
                )}
              </Typography>
            </Box>
          </TableCell>
          <TableCell align={transactionAlignById.price_per_litre} sx={{ fontSize: { sm: '0.813rem', md: '0.875rem' } }}>
            {transaction.price_per_litre}
          </TableCell>
          <TableCell align={transactionAlignById.supplier} sx={{ fontSize: { sm: '0.813rem', md: '0.875rem' } }}>
            {transaction.supplier}
          </TableCell>
          <TableCell align={transactionAlignById.received_tank_name} sx={{ fontSize: { sm: '0.813rem', md: '0.875rem' } }}>
            {transaction.received_tank_name || 'N/A'}
          </TableCell>
        </>
      );
    }
    const isTransferType = transactionType === TRANSACTION_TYPES[2].id;
    return (
      <>
        <TableCell align={transactionAlignById.order_id} sx={{ fontSize: { sm: '0.813rem', md: '0.875rem' } }}>
          {transaction.order_id}
        </TableCell>
        <TableCell align={transactionAlignById.end_time} sx={{ fontSize: { sm: '0.813rem', md: '0.875rem' } }}>
          {getFormattedDate(transaction.start_time)}
        </TableCell>
        <TableCell align={transactionAlignById.site_name} sx={{ fontSize: { sm: '0.813rem', md: '0.875rem' } }}>
          {transaction.site_name}
        </TableCell>
        <TableCell align={transactionAlignById.address} sx={{ fontSize: { sm: '0.813rem', md: '0.875rem' } }}>
          {transaction.address}
        </TableCell>
        <TableCell align={transactionAlignById.nozzle_num} sx={{ fontSize: { sm: '0.813rem', md: '0.875rem' } }}>
          {transaction.nozzle_num}
        </TableCell>
        {isTransferType && (
          <TableCell align={transactionAlignById.card_num_1} sx={{ fontSize: { sm: '0.813rem', md: '0.875rem' } }}>
            {removeLeadingZeros(transaction.card_num_1) || 'N/A'}
          </TableCell>
        )}
        {isTransferType && (
          <TableCell align={transactionAlignById.card_num_2} sx={{ fontSize: { sm: '0.813rem', md: '0.875rem' } }}>
            {removeLeadingZeros(transaction.card_num_2) || 'N/A'}
          </TableCell>
        )}
        <TableCell align={transactionAlignById.vehicle_number} sx={{ fontSize: { sm: '0.813rem', md: '0.875rem' } }}>
          {transaction.vehicle_number}
        </TableCell>
        <TableCell align={transactionAlignById.odometer_entry} sx={{ fontSize: { sm: '0.813rem', md: '0.875rem' } }}>
          {transaction.odometer_entry ?? 'N/A'}
        </TableCell>
        <TableCell align={transactionAlignById.quantity}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: { sm: '0.875rem', md: '0.938rem' },
              }}
            >
              {getFuelQuantity(transaction.quantity)}
            </Typography>
          </Box>
        </TableCell>
      </>
    );
  };

  // Get sorted data
  const displayData = transactionList?.slice().sort(getComparator(order, orderBy)) || [];

  // Determine which mobile card to use
  const isPurchaseType = transactionType === TRANSACTION_TYPES[1].id;

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          {/* Mobile View: Card Layout */}
          {isMobile ? (
            <Box sx={{ width: '100%' }}>
              {displayData.length > 0 ? (
                displayData.map((transaction) => (isPurchaseType ? (
                  <PurchaseMobileCard
                    key={transaction.id}
                    transaction={transaction}
                    onViewClick={viewTransactionInfo}
                    theme={theme}
                    disabled={isDetailsDisabled()}
                  />
                ) : (
                  <SalesMobileCard
                    key={transaction.id}
                    transaction={transaction}
                    onViewClick={viewTransactionInfo}
                    theme={theme}
                    disabled={isDetailsDisabled()}
                  />
                )))
              ) : (
                <Card sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No transactions found
                  </Typography>
                </Card>
              )}
              <CustomPagination
                page={page}
                rowsPerPage={limit}
                count={totalCount}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleLimitChange}
              />
            </Box>
          ) : (
            /* Desktop/Tablet View: Table Layout */
            <Card
              sx={{
                borderRadius: 3,
                background: '#FFFFFF',
                border: `1px solid ${theme.palette.divider}`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  minHeight: 0,
                  flex: 1,
                  overflowX: 'auto',
                  overflowY: 'auto',
                  '& .MuiTableCell-stickyHeader': {
                    backgroundColor: '#F9FAFB',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    zIndex: 2
                  },
                  '& .MuiTableHead-root .MuiTableCell-root:last-of-type': {
                    position: 'sticky',
                    right: 0,
                    zIndex: 3,
                    backgroundColor: '#F9FAFB',
                    boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                <Box sx={{ minWidth: isTablet ? 800 : 1050 }}>
                  <Table stickyHeader sx={{ tableLayout: 'fixed', minWidth: isTablet ? 800 : 1050 }}>
                    <EnhancedTableHead
                      order={order}
                      orderBy={orderBy}
                      headCells={activeTransactionHeaders}
                      onRequestSort={handleRequestSort}
                      sx={{
                        backgroundColor: '#F9FAFB',
                        borderBottom: `1px solid ${theme.palette.divider}`
                      }}
                    />
                    <TableBody>
                      {displayData.length > 0 ? (
                        displayData.map((transaction) => (
                          <TableRow
                            hover
                            key={transaction.id}
                            sx={{
                              transition: 'background-color 0.3s, box-shadow 0.3s',
                              '&:hover': {
                                backgroundColor: '#f5f7fa',
                                boxShadow: theme.shadows[2],
                              },
                            }}
                          >
                            {renderTableData(transaction)}
                            <TableCell
                              align={transactionAlignById.action}
                              sx={{
                                position: 'sticky',
                                right: 0,
                                zIndex: 1,
                                backgroundColor: '#FFFFFF',
                                boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
                              }}
                            >
                              <Tooltip title="View Details">
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    viewTransactionInfo(transaction.id);
                                  }}
                                  disabled={isDetailsDisabled()}
                                  size="medium"
                                  aria-label="View details"
                                  sx={getTableActionIconButtonSx(theme)}
                                >
                                  <ChevronRightIcon sx={TABLE_ACTION_ICON_SX} />
                                </IconButton>
                              </Tooltip>

                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={activeTransactionHeaders.length || 1}
                            align="center"
                            sx={{ py: 4 }}
                          >
                            <Typography variant="body1" color="text.secondary">
                              No transactions found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
              <Divider sx={{ flexShrink: 0 }} />
              <Box sx={{ flexShrink: 0, bgcolor: '#FFFFFF' }}>
                <CustomPagination
                  page={page}
                  rowsPerPage={limit}
                  count={totalCount}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleLimitChange}
                />
              </Box>
            </Card>
          )}
        </>
      )}
    </>
  );
};

TransactionListTable.propTypes = {
  refreshData: PropTypes.bool,
  isSales: PropTypes.bool,
  searchText: PropTypes.string,
  dateFilter: PropTypes.string,
  siteFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default TransactionListTable;
